import { Message } from "@hermes/intl";
import { cssFns, useColors, useScreenDetector } from "@hermes/ui";

import { type DoctorCardOptions, DoctorCardV2 } from "../doctor-card/v2";
import { AdditionalListWrapper } from "./additional-list";
import { type DoctorsListParams, useDoctorsListData } from "./data";
import { DoctorsListLoading } from "./loading";

export const DoctorsListV2 = ({
  params,
  options,
}: {
  params: DoctorsListParams;
  options?: {
    displayCount?: boolean;
    displayAllReviews?: boolean;
    pageHeader: string;
    displaySpecialties?: boolean;
    displayAdditionalList?: boolean;
  };
}) => {
  const colors = useColors();
  const { isMobile } = useScreenDetector();
  const {
    count,
    pages,
    premiumList,
    slotsPages,
    isInitialFetching,
    limit,
    fetchPage,
    doctorIds,
  } = useDoctorsListData({ params });

  const FETCH_ON_INDEX = isMobile ? 3 : 6;

  const isLastPageSeen = count ? pages[pages.length - 1]?.seen === true : false;

  const commonOptions: DoctorCardOptions = {
    displayCityArea: !!params.cityArea,
    displayAllReviews: options?.displayAllReviews,
    heading: params.clinicId ? "h3" : undefined,
    noRedirectToClinic: !!params.clinicId,
    targetServicesIds: params.targetServices,
    displaySpecialties: options?.displaySpecialties,
    clinicId: params.clinicId,
    isBordered: params.clinicId ? true : undefined,
  };

  return (
    <div>
      {options?.displayCount && (
        <div css={{ margin: isMobile ? "0 0 10px" : "0 0 20px" }}>
          <h2
            css={[
              { margin: 0, color: colors.principal.gs4 },
              cssFns.typo({ level: "body-1", weight: "regular" }),
            ]}
          >
            <Message
              id="2c5660dc-ff4f-4326-a6ba-93d3dba66f13"
              default="Найдено врачей: {count}"
              values={{ count }}
            />
          </h2>
        </div>
      )}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          rowGap: 16,
        }}
      >
        {isInitialFetching ? (
          <DoctorsListLoading length={limit} />
        ) : (
          <>
            {premiumList.results?.map((doctor) => {
              return (
                <DoctorCardV2
                  key={doctor.id}
                  doctor={doctor}
                  slots={{
                    defaultStartDate: params.slotDate,
                    initial: slotsPages[0]?.results?.filter(
                      (slot) => slot.doctor === doctor.id,
                    ),
                    isFetching: slotsPages[0]?.isFetching,
                  }}
                  options={{
                    premium: true,
                    request_meta: {
                      booking_page: options?.pageHeader,
                      list_position: 0,
                    },
                    ...commonOptions,
                  }}
                />
              );
            })}
            {pages.map((page, pageIndex) => {
              if (!page.seen) return null;
              if (page.isFetching) {
                const remaining = count ? count - pageIndex * limit : undefined;
                const pageSize = remaining
                  ? remaining >= limit
                    ? limit
                    : remaining
                  : limit;
                return (
                  <DoctorsListLoading
                    key={pageIndex}
                    // As we load the last page, the limit might be higher than the actual page size
                    // We get the page size from the remaining number of data to load
                    // This should affect only the last page
                    // As a result, while users scrolls, we render the exact number of skeletons so that the page won't jump around
                    length={pageSize}
                  />
                );
              }
              return page.results?.map((doctor, index) => {
                const pageSlots = slotsPages[pageIndex + 1];

                const shouldTriggerFetch = index + 1 >= FETCH_ON_INDEX;
                return (
                  <DoctorCardV2
                    key={doctor.id}
                    doctor={doctor}
                    slots={{
                      defaultStartDate: params.slotDate,
                      initial: pageSlots?.results?.filter(
                        (slot) => slot.doctor === doctor.id,
                      ),
                      isFetching: pageSlots?.isFetching,
                    }}
                    options={{
                      request_meta: {
                        booking_page: options?.pageHeader,
                        list_position: pageIndex * limit + index + 1,
                      },
                      ...commonOptions,
                    }}
                    onInView={() => {
                      if (shouldTriggerFetch) {
                        fetchPage(pageIndex + 1);
                      }
                    }}
                  />
                );
              });
            })}
            {options?.displayAdditionalList && isLastPageSeen && (
              <AdditionalListWrapper
                city={params.city}
                cityArea={params.cityArea}
                insurance={params.insurance}
              >
                <DoctorsListV2
                  params={{
                    ...params,
                    nearestCityArea: params.cityArea
                      ? params.cityArea.id
                      : undefined,
                    ...(params.insurance && doctorIds.length > 0
                      ? { excludedIds: doctorIds }
                      : {}),
                  }}
                  options={{
                    ...options,
                    // Prevent multiple recursive rerenders
                    displayAdditionalList: false,
                  }}
                />
              </AdditionalListWrapper>
            )}
          </>
        )}
      </div>

      {count === 0 && (
        <div
          css={[
            cssFns.typo({ level: "title-4" }),
            { textAlign: "center", marginBlockEnd: 200 },
          ]}
        >
          <Message
            id="3b187e89-5f24-429f-a31d-4991958c3fb0"
            default="No doctors were found for your query."
          />
        </div>
      )}
    </div>
  );
};
